import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { IconName } from '../../components/icons/Icon'
import { ListView } from './ListView'
import { rsqlAnd, rsqlSearch } from '../../lib/rsql'
import { usePersistentState } from '../../lib/usePersistentState'
import { useDebouncedValue } from '../../lib/useDebouncedValue'
import { useSavedViews } from '../../lib/savedViews'
import { runExport, type ExportColumn } from '../../lib/export'
import { ApiError } from '../../api/problem'
import type { ColumnDef, Density, ExportFormat, SortSpec } from './types'

interface RelatedListSavedViewState {
  search: string
  sort: SortSpec[]
  columnOrder: string[]
  hiddenColumnIds: string[]
  pinnedColumnIds: string[]
  density: Density
}

export interface RelatedListPage<T> {
  items: T[]
  nextCursor: string | null
}

export interface RelatedListProps<T> {
  /** Namespaces this list's persisted column/density prefs (e.g. `iw.account-detail.legal-entities`). */
  storageKeyPrefix: string
  entityIcon: IconName
  title: string
  columns: ColumnDef<T>[]
  defaultColumnOrder: string[]
  defaultSort?: SortSpec[]
  /** RSQL fields the free-text search box wildcard-matches; omit to hide the search box. */
  searchFields?: string[]
  searchPlaceholder?: string
  /** The parent-scoping RSQL clause (e.g. `accountId==...`), AND-combined with search. Always applied. */
  parentFilter: string
  fetchPage: (args: { filter: string | null; sort: string | null; cursor: string | null; limit: number }) => Promise<RelatedListPage<T>>
  getRowId: (row: T) => string
  onRowActivate?: (row: T) => void
  emptyTitle: string
  emptyDescription?: string
}

const PAGE_LIMIT = 50

/**
 * The M23 universal control set (docs/design-system.md §3.2), reused for an EMBEDDED,
 * parent-scoped list rather than hand-rolled per detail page (docs/design/
 * IB-49-crm-detail-timeline-design.md §5.2 — FR-M5.2's AC is explicit: "Every related list carries
 * the M23 universal control set"). One real `ListView` instance underneath: filter (parent scope +
 * search), sort, columns (show/hide/reorder/pin), density, personal saved views (named, browser-
 * local — see `lib/savedViews.ts`), CSV/
 * XLSX/PDF export, and cursor "Load more" pagination — the same contract `AccountsListScreen`/
 * `BranchesListScreen` already give a top-level list, generic over `T` so Account→LegalEntities,
 * LegalEntity→Branches and LegalEntity→Cases (the three IB-49 embeddings) share one implementation.
 */
export function RelatedList<T>({
  storageKeyPrefix,
  entityIcon,
  title,
  columns,
  defaultColumnOrder,
  defaultSort,
  searchFields,
  searchPlaceholder,
  parentFilter,
  fetchPage,
  getRowId,
  onRowActivate,
  emptyTitle,
  emptyDescription,
}: RelatedListProps<T>) {
  const [columnOrder, setColumnOrder] = usePersistentState<string[]>(`${storageKeyPrefix}.columnOrder`, defaultColumnOrder)
  const [hiddenColumnIdsArr, setHiddenColumnIdsArr] = usePersistentState<string[]>(
    `${storageKeyPrefix}.hiddenColumns`,
    columns.filter((c) => c.defaultHidden).map((c) => c.id),
  )
  const [pinnedColumnIdsArr, setPinnedColumnIdsArr] = usePersistentState<string[]>(`${storageKeyPrefix}.pinnedColumns`, [])
  const [density, setDensity] = usePersistentState<Density>(`${storageKeyPrefix}.density`, 'compact')

  const hiddenColumnIds = useMemo(() => new Set(hiddenColumnIdsArr), [hiddenColumnIdsArr])
  const pinnedColumnIds = useMemo(() => new Set(pinnedColumnIdsArr), [pinnedColumnIdsArr])

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const [sort, setSort] = useState<SortSpec[]>(defaultSort ?? [])
  const [selection, setSelection] = useState<Set<string>>(new Set())

  const [rows, setRows] = useState<T[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filterString = useMemo(
    () => rsqlAnd(parentFilter, searchFields ? rsqlSearch(searchFields, debouncedSearch) : null) || null,
    [parentFilter, searchFields, debouncedSearch],
  )
  const sortString = useMemo(
    () => (sort.length === 0 ? null : sort.map((s) => (s.direction === 'desc' ? `-${s.columnId}` : s.columnId)).join(',')),
    [sort],
  )
  const latestRequestRef = useRef(0)

  const load = useCallback(
    async (afterCursor: string | null, replace: boolean) => {
      const requestId = ++latestRequestRef.current
      try {
        if (replace) {
          setLoading(true)
          setError(null)
        } else {
          setLoadingMore(true)
        }
        const page = await fetchPage({ filter: filterString, sort: sortString, cursor: afterCursor, limit: PAGE_LIMIT })
        if (latestRequestRef.current !== requestId) return
        setRows((prev) => (replace ? page.items : [...prev, ...page.items]))
        setCursor(page.nextCursor)
      } catch (err) {
        if (latestRequestRef.current !== requestId) return
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : `Could not load ${title.toLowerCase()}.`)
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [fetchPage, filterString, sortString, title],
  )

  useEffect(() => {
    setSelection(new Set())
    void load(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterString, sortString])

  function toggleColumnHidden(id: string) {
    setHiddenColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  function moveColumn(id: string, direction: -1 | 1) {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const tmp = next[index]!
      next[index] = next[target]!
      next[target] = tmp
      return next
    })
  }
  function toggleColumnPin(id: string) {
    setPinnedColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }

  const savedViewState: RelatedListSavedViewState = useMemo(
    () => ({ search, sort, columnOrder, hiddenColumnIds: hiddenColumnIdsArr, pinnedColumnIds: pinnedColumnIdsArr, density }),
    [search, sort, columnOrder, hiddenColumnIdsArr, pinnedColumnIdsArr, density],
  )
  const applySavedViewState = useCallback((state: RelatedListSavedViewState) => {
    setSearch(state.search)
    setSort(state.sort)
    setColumnOrder(state.columnOrder)
    setHiddenColumnIdsArr(state.hiddenColumnIds)
    setPinnedColumnIdsArr(state.pinnedColumnIds)
    setDensity(state.density)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const savedViews = useSavedViews<RelatedListSavedViewState>(storageKeyPrefix, savedViewState, applySavedViewState)

  const exportColumns: ExportColumn<T>[] = useMemo(
    () =>
      columnOrder
        .map((id) => columns.find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined && !hiddenColumnIds.has(c.id))
        .map((c) => ({ id: c.id, header: c.header, toText: c.toExportText })),
    [columnOrder, hiddenColumnIds, columns],
  )

  function handleExport(format: ExportFormat) {
    runExport(format, title, rows, exportColumns)
  }
  function handleExportSelected(selectedIds: string[]) {
    const idSet = new Set(selectedIds)
    runExport('csv', `${title} (selected)`, rows.filter((r) => idSet.has(getRowId(r))), exportColumns)
  }

  const hasFilterOrSearch = debouncedSearch.trim() !== ''
  const state: 'loading' | 'error' | 'empty-tenant' | 'empty-filtered' | 'ready' = error
    ? 'error'
    : loading
      ? 'loading'
      : rows.length === 0
        ? hasFilterOrSearch
          ? 'empty-filtered'
          : 'empty-tenant'
        : 'ready'

  return (
    <ListView<T>
      entityIcon={entityIcon}
      title={title}
      countLabel={loading ? undefined : `${rows.length}${cursor ? '+' : ''}`}
      quickFilters={[]}
      activeQuickFilterIds={new Set()}
      onQuickFilterToggle={() => {}}
      search={search}
      onSearchChange={setSearch}
      searchLabel={`Search ${title.toLowerCase()}`}
      searchPlaceholder={searchPlaceholder}
      filterChildren={null}
      activeFilterCount={0}
      activeFilterChips={[]}
      onRemoveFilterChip={() => {}}
      onClearFilters={() => setSearch('')}
      savedViews={savedViews}
      onExport={handleExport}
      columns={columns}
      columnOrder={columnOrder}
      hiddenColumnIds={hiddenColumnIds}
      pinnedColumnIds={pinnedColumnIds}
      onToggleColumnHidden={toggleColumnHidden}
      onMoveColumn={moveColumn}
      onToggleColumnPin={toggleColumnPin}
      sort={sort}
      onSortChange={setSort}
      density={density}
      onDensityChange={setDensity}
      state={state}
      rows={rows}
      getRowId={getRowId}
      onRowActivate={onRowActivate}
      selection={selection}
      onSelectionChange={setSelection}
      bulkActions={[{ id: 'export-selected', label: 'Export selected', icon: 'download', onRun: handleExportSelected }]}
      errorMessage={error ?? undefined}
      onRetry={() => void load(null, true)}
      emptyTenant={{ icon: entityIcon, title: emptyTitle, description: emptyDescription }}
      hasMore={Boolean(cursor)}
      onLoadMore={() => void load(cursor, false)}
      loadingMore={loadingMore}
    />
  )
}
